"use client";
import { Button, Card, ScreenLoader } from "@/components";
import { useFormStepThree } from "@/hooks";
import { ExperienceForm, EducationForm } from "./components";
import { OptionsJobber } from "@/types";
import Image from "next/image";
import { FormProvider } from "react-hook-form";
import { LuPlus } from "react-icons/lu";
import React from "react";
import { useTranslations } from "next-intl";

type FormStepThreeProp = {
  optionsJobber: {
    options?: OptionsJobber;
    optionError: boolean;
    optionIsLoading: boolean;
  };
  userId: string;
  buttonText?: string;
  skipRedirect?: boolean;
};

export default function FormStepThree({
  optionsJobber,
  userId,
  buttonText,
  skipRedirect = false,
}: FormStepThreeProp) {
  const {
    methods,
    watch,
    workExperienceFields,
    educationFields,
    collapsedForms,
    collapsedEducationForms,
    showCollapse,
    showEducationCollapse,
    confirmedExperiences,
    confirmedEducations,
    isExperienceComplete,
    isEducationComplete,
    confirmExperience,
    confirmEducation,
    editExperience,
    editEducation,
    appendWorkExperience,
    removeWorkExperience,
    appendEducation,
    removeEducation,
    setCollapsedForms,
    setCollapsedEducationForms,
    setShowCollapse,
    setShowEducationCollapse,
    onSubmit,
    hasAnyUnconfirmed,
    getUnconfirmedIndices,
  } = useFormStepThree({ userId, skipRedirect });

  const t = useTranslations("jobberProfile.forms.stepThree");
  const [validateSignal, setValidateSignal] = React.useState(0);
  const [confirmReminderSignal, setConfirmReminderSignal] = React.useState(0);
  const anyExperienceInvalid = workExperienceFields.some((_, i) => !isExperienceComplete(i));
  const anyEducationInvalid = educationFields.some((_, i) => !isEducationComplete(i));
  const hasUnconfirmed = hasAnyUnconfirmed();
  const unconfirmedIndices = React.useMemo(() => getUnconfirmedIndices(), [getUnconfirmedIndices]);

  return (
    <FormProvider {...methods}>
      {methods.formState.isSubmitting && <ScreenLoader />}
      <form className="pb-3" onSubmit={methods.handleSubmit(onSubmit)}>
        <Card defaultStyle={false} className="p-0">
          <div className=" grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="font-semibold text-xl md:text-2xl mb-4">
                {t("workExperience")}
              </h2>

              {workExperienceFields.map((field, index) => {
                const isCollapsed = collapsedForms.has(index);
                const shouldShowCollapse = showCollapse.has(index);
                const isLastExperience =
                  index === workExperienceFields.length - 1;
                const hasData =
                  !!watch(`experiences.${index}.job_title`) ||
                  !!watch(`experiences.${index}.company_name`) ||
                  !!watch(`experiences.${index}.start_date`) ||
                  isLastExperience;

                const isComplete = isExperienceComplete(index);

                return (
                  <ExperienceForm
                    key={field.id}
                    index={index}
                    optionsJobber={optionsJobber}
                    isCollapsed={isCollapsed}
                    shouldShowCollapse={shouldShowCollapse}
                    hasData={hasData}
                    isComplete={isComplete}
                    isConfirmed={confirmedExperiences.has(index)}
                    onToggleCollapse={() => {
                      if (isCollapsed) {
                        // Expand this experience entry and collapse ALL other entries (both experience and education)
                        const newCollapsed = new Set<number>();
                        workExperienceFields.forEach((_, i) => {
                          if (i !== index) {
                            newCollapsed.add(i);
                          }
                        });
                        setCollapsedForms(newCollapsed);

                        // Collapse ALL education entries
                        const newEducationCollapsed = new Set<number>();
                        educationFields.forEach((_, i) => {
                          newEducationCollapsed.add(i);
                        });
                        setCollapsedEducationForms(newEducationCollapsed);
                      } else {
                        // Collapse this entry
                        setCollapsedForms((prev) => new Set(prev).add(index));
                      }
                    }}
                    onRemove={() => removeWorkExperience(index)}
                    onConfirm={() => confirmExperience(index)}
                    onEdit={() => editExperience(index)}
                    validateSignal={validateSignal}
                    confirmReminderSignal={confirmReminderSignal}
                    highlightUnconfirmed={unconfirmedIndices.experiences.includes(index)}
                  />
                );
              })}

              <div className="flex justify-start mt-4">
                <button
                  type="button"
                  onClick={() => {

                    const newIndex = workExperienceFields.length;

                    // Collapse ALL other entries (both experience and education)
                    const newCollapsed = new Set<number>();
                    workExperienceFields.forEach((_, i) => {
                      newCollapsed.add(i);
                    });
                    setCollapsedForms(newCollapsed);

                    // Collapse ALL education entries
                    const newEducationCollapsed = new Set<number>();
                    educationFields.forEach((_, i) => {
                      newEducationCollapsed.add(i);
                    });
                    setCollapsedEducationForms(newEducationCollapsed);

                    appendWorkExperience({
                      job_title: "",
                      company_name: "",
                      description: "",
                      category: { id: "", name: "" },
                      specialization: { id: "", name: "" },
                      skills_experiences: [],
                      start_date: "",
                      end_date: "",
                      work_mode: { id: "", name: "" },
                      company_industry: { id: "", name: "" },
                    });

                    // Show the new form expanded
                    setShowCollapse((prev) => new Set(prev).add(newIndex));
                  }}
                  className="flex items-center gap-2 px-6 py-2 tracking-wide transition-all duration-200 transform focus:outline-none focus:ring focus:ring-opacity-50 rounded-2xl text-white hover:opacity-90 focus:ring-brand-300 cursor-pointer"
                  style={{
                    background: 'linear-gradient(83deg, #5B02FF 14.86%, #8F36FF 98.61%)'
                  }}
                  title={t("addWorkExperience")}
                >
                  {t("addWorkExperience")}
                </button>
              </div>
              {/* Removed gating helper message */}
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="font-semibold text-xl md:text-2xl mb-4">{t("education")}</h2>

              {educationFields.map((field, index) => {
                const isCollapsed = collapsedEducationForms.has(index);
                const shouldShowCollapse = showEducationCollapse.has(index);
                const isLastEducation = index === educationFields.length - 1;
                const hasData =
                  !!watch(`educations.${index}.certification_title`) ||
                  !!watch(`educations.${index}.institution_name`) ||
                  !!watch(`educations.${index}.start_date`) ||
                  isLastEducation;
                const isComplete = isEducationComplete(index);

                return (
                  <EducationForm
                    key={field.id}
                    index={index}
                    optionsJobber={optionsJobber}
                    isCollapsed={isCollapsed}
                    shouldShowCollapse={shouldShowCollapse}
                    hasData={hasData}
                    isComplete={isComplete}
                    isConfirmed={confirmedEducations.has(index)}
                    onToggleCollapse={() => {
                      if (isCollapsed) {
                        // Expand this education entry and collapse ALL other entries (both experience and education)
                        const newEducationCollapsed = new Set<number>();
                        educationFields.forEach((_, i) => {
                          if (i !== index) {
                            newEducationCollapsed.add(i);
                          }
                        });
                        setCollapsedEducationForms(newEducationCollapsed);

                        // Collapse ALL experience entries
                        const newCollapsed = new Set<number>();
                        workExperienceFields.forEach((_, i) => {
                          newCollapsed.add(i);
                        });
                        setCollapsedForms(newCollapsed);
                      } else {
                        // Collapse this entry
                        setCollapsedEducationForms((prev) => new Set(prev).add(index));
                      }
                    }}
                    onRemove={() => removeEducation(index)}
                    onConfirm={() => confirmEducation(index)}
                    onEdit={() => editEducation(index)}
                    validateSignal={validateSignal}
                    confirmReminderSignal={confirmReminderSignal}
                    highlightUnconfirmed={unconfirmedIndices.educations.includes(index)}
                  />
                );
              })}

              <div className="flex justify-start mt-4">
                <button
                  type="button"
                  onClick={() => {

                    const newIndex = educationFields.length;

                    // Collapse ALL other entries (both experience and education)
                    const newEducationCollapsed = new Set<number>();
                    educationFields.forEach((_, i) => {
                      newEducationCollapsed.add(i);
                    });
                    setCollapsedEducationForms(newEducationCollapsed);

                    // Collapse ALL experience entries
                    const newCollapsed = new Set<number>();
                    workExperienceFields.forEach((_, i) => {
                      newCollapsed.add(i);
                    });
                    setCollapsedForms(newCollapsed);

                    appendEducation({
                      certification_title: "",
                      institution_name: "",
                      category: { id: "", name: "" },
                      skills_education: [],
                      start_date: "",
                      end_date: "",
                      certification_type: { id: "", name: "" },
                    });

                    // Show the new form expanded
                    setShowEducationCollapse((prev) => new Set(prev).add(newIndex));
                  }}
                  className="flex items-center gap-2 px-6 py-2 tracking-wide transition-all duration-200 transform focus:outline-none focus:ring focus:ring-opacity-50 rounded-2xl text-white hover:opacity-90 focus:ring-brand-300 cursor-pointer"
                  style={{
                    background: 'linear-gradient(83deg, #5B02FF 14.86%, #8F36FF 98.61%)'
                  }}
                  title={t("addEducation")}
                >
                  
                  {t("addEducation")}
                </button>
              </div>
              {/* Removed gating helper message */}
            </div>
          </div>
        </Card>
        <div className="mt-6 flex justify-center sm:justify-end">
          <Button
            type="submit"
            variant="none"
            rounded="rounded-xl"
            className="w-full sm:w-[208px] text-white hover:text-white hover:opacity-90 py-2 px-7 border-0"
            style={{
              background: 'linear-gradient(83deg, #5B02FF 14.86%, #8F36FF 98.61%)'
            }}
            isLoading={methods.formState.isSubmitting}
            onClick={(event) => {
              // Check if there are any form errors (including validation errors)
              const hasFormErrors = Object.keys(methods.formState.errors).length > 0;
              
              // If there are form errors, expand the forms that have errors
              if (hasFormErrors) {
                const errors = methods.formState.errors;
                
                // Check for experience errors and expand those forms
                if (errors.experiences && Array.isArray(errors.experiences)) {
                  const newCollapsed = new Set(collapsedForms);
                  errors.experiences.forEach((error, index) => {
                    if (error && typeof error === 'object') {
                      newCollapsed.delete(index); // Remove from collapsed set to expand
                      setShowCollapse((prev) => new Set(prev).add(index)); // Show collapse controls
                    }
                  });
                  setCollapsedForms(newCollapsed);
                }
                
                // Check for education errors and expand those forms
                if (errors.educations && Array.isArray(errors.educations)) {
                  const newEducationCollapsed = new Set(collapsedEducationForms);
                  errors.educations.forEach((error, index) => {
                    if (error && typeof error === 'object') {
                      newEducationCollapsed.delete(index); // Remove from collapsed set to expand
                      setShowEducationCollapse((prev) => new Set(prev).add(index)); // Show collapse controls
                    }
                  });
                  setCollapsedEducationForms(newEducationCollapsed);
                }
              }
              
              // Request child groups to show validation if any invalid
              if (anyExperienceInvalid || anyEducationInvalid || hasUnconfirmed || hasFormErrors) {
                setValidateSignal((s) => s + 1);
                if (hasUnconfirmed) {
                  setConfirmReminderSignal((s) => s + 1);
                }
                event.preventDefault();
                
                // If there are form errors, let's try to trigger validation display
                if (hasFormErrors) {
                  // Force validation to show errors
                  methods.trigger();
                }
              }
            }}
          >
            {buttonText || t('next')}
          </Button>
        </div>
        <div className="relative hidden md:block">
          <Image
            alt="ellipse"
            src={"/images/jobbi/man_profile.png"}
            width={0}
            height={0}
            sizes="100vw"
            className="absolute top-10 md:top-5 left-[-5px]  md:left-[-30px] w-[60px] md:max-w-[100px] h-auto"
            unoptimized
            priority
          />
          <div className="bg-brand-25 rounded-2xl p-4 max-w-lg text-center text-xs text-black">
            {t("profileCompletionMessage")}
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
